import React, { useState, useEffect, useCallback } from 'react';
import { meetingApi, projectApi, userApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, PencilSimple, CheckCircle, Clock, User } from '@phosphor-icons/react';

export default function MeetingNotesPage() {
  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ project_id: '', title: '', date: '', attendees: [], notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showActionModal, setShowActionModal] = useState(false);
  const [actionMeeting, setActionMeeting] = useState(null);
  const [actionForm, setActionForm] = useState({ description: '', assigned_to: '', deadline: '' });

  useEffect(() => {
    Promise.all([projectApi.list(), userApi.list()])
      .then(([p, u]) => { setProjects(p.data); setUsers(u.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadMeetings = useCallback(async () => {
    const res = await meetingApi.list(filterProject || undefined);
    setMeetings(res.data);
  }, [filterProject]);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  const openCreate = () => {
    setEditing(null);
    setForm({ project_id: filterProject || '', title: '', date: new Date().toISOString().split('T')[0], attendees: [], notes: '' });
    setError(''); setShowModal(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ project_id: m.project_id, title: m.title, date: m.date, attendees: m.attendees || [], notes: m.notes || '' });
    setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      if (editing) await meetingApi.update(editing.id, form);
      else await meetingApi.create(form);
      setShowModal(false); loadMeetings();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try { await meetingApi.delete(id); loadMeetings(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const openAddAction = (meeting) => {
    setActionMeeting(meeting);
    setActionForm({ description: '', assigned_to: '', deadline: '' });
    setShowActionModal(true);
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    try {
      await meetingApi.addAction(actionMeeting.id, actionForm);
      setShowActionModal(false); loadMeetings();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleAction = async (meetingId, actionId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await meetingApi.updateAction(meetingId, actionId, { status: newStatus });
      loadMeetings();
    } catch (err) { alert('Failed to update'); }
  };

  const getUserName = (id) => users.find(u => u.id === id)?.name || id || 'Unassigned';

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="meetings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Meeting Notes</h1>
          <p className="text-sm text-[#666]">Record meetings, assign actions & track deadlines</p>
        </div>
        <Button onClick={openCreate} data-testid="add-meeting-btn"><Plus className="w-4 h-4 mr-2" /> New Meeting</Button>
      </div>

      <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="w-64" data-testid="filter-project">
        <option value="">All Projects</option>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select>

      {meetings.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No meeting notes yet</Card>
      ) : (
        <div className="space-y-3">
          {meetings.map(m => (
            <Card key={m.id} className="p-4" data-testid={`meeting-row-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{m.title}</span>
                    <span className="text-xs text-[#666]">{m.date}</span>
                    <span className="text-xs text-[#999]">{m.project_name}</span>
                  </div>
                  {m.attendees?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <User className="w-3 h-3 text-[#999]" />
                      <span className="text-xs text-[#666]">{m.attendees.map(a => getUserName(a)).join(', ')}</span>
                    </div>
                  )}
                  {m.notes && <p className="text-sm text-[#666] mt-2 whitespace-pre-wrap bg-[#F9F9F9] p-2 rounded">{m.notes}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(m)} className="p-1 text-[#666] hover:text-[#002FA7]"><PencilSimple className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(m.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-meeting-${m.id}`}><Trash className="w-4 h-4" /></button>
                </div>
              </div>

              {/* Action Items */}
              <div className="mt-3 border-t border-[#F0F0F0] pt-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-[#999] uppercase">Action Items ({m.action_items?.length || 0})</p>
                  <button onClick={() => openAddAction(m)} className="text-xs text-[#002FA7] hover:underline" data-testid={`add-action-${m.id}`}>+ Add Action</button>
                </div>
                {m.action_items?.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1 border-b border-[#F5F5F5] last:border-0">
                    <button onClick={() => handleToggleAction(m.id, a.id, a.status)} data-testid={`toggle-action-${a.id}`}>
                      {a.status === 'completed' ? <CheckCircle className="w-4 h-4 text-[#00C853]" weight="fill" /> : <Clock className="w-4 h-4 text-[#999]" />}
                    </button>
                    <span className={`flex-1 text-sm ${a.status === 'completed' ? 'line-through text-[#999]' : ''}`}>{a.description}</span>
                    {a.assigned_to && <span className="text-xs text-[#666]">{getUserName(a.assigned_to)}</span>}
                    {a.deadline && <span className="text-xs text-[#999]">Due: {a.deadline}</span>}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Meeting Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Meeting' : 'New Meeting'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="meeting-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required data-testid="meeting-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="meeting-title" />
          <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="meeting-date" />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-[#0A0A0A]">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm min-h-[100px]" data-testid="meeting-notes" />
          </div>
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-meeting-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>

      {/* Action Item Modal */}
      <Modal isOpen={showActionModal} onClose={() => setShowActionModal(false)} title="Add Action Item">
        <form onSubmit={handleAddAction} className="space-y-3" data-testid="action-form">
          <Input label="Description" value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} required data-testid="action-desc" />
          <Select label="Assigned To" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })} data-testid="action-assignee">
            <option value="">-- Unassigned --</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          <Input label="Deadline" type="date" value={actionForm.deadline} onChange={(e) => setActionForm({ ...actionForm, deadline: e.target.value })} data-testid="action-deadline" />
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowActionModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-action-btn">Add</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
